"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/stores/cookie-consent-store";

// Re-export for backwards compat with any other consumers
export { useCookieConsent } from "@/stores/cookie-consent-store";
export type { ConsentLevel } from "@/stores/cookie-consent-store";
export { COOKIE_CONSENT_KEY } from "@/stores/cookie-consent-store";

/**
 * Hydrates cookie consent from the browser cookie and renders the banner
 * when the user hasn't made a choice yet. Drop-in replacement for the old
 * CookieConsentProvider — no longer wraps children.
 */
export function CookieConsentBanner() {
  const consent = useCookieConsent((s) => s.consent);
  const mounted = useCookieConsent((s) => s.mounted);
  const setConsent = useCookieConsent((s) => s.setConsent);
  const hydrate = useCookieConsent((s) => s.hydrate);

  useEffect(() => {
    queueMicrotask(hydrate);
  }, [hydrate]);

  if (!mounted || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-45 border-t border-border bg-card px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg sm:left-4 sm:right-4 sm:bottom-4 sm:pb-4 sm:max-w-lg sm:rounded-xl"
    >
      <p className="text-sm text-foreground">
        We use essential cookies for the site to work and optional cookies for
        analytics. You can choose which to accept.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setConsent("all")}>
          Accept all
        </Button>
        <Button size="sm" variant="outline" onClick={() => setConsent("essential")}>
          Essential only
        </Button>
        <Link
          href="/privacy#cookies"
          className="text-sm text-muted-foreground underline hover:text-foreground"
        >
          Cookie policy
        </Link>
      </div>
    </div>
  );
}
